/**
 * Lab member Net Exposure merge: sportsbook + Premium BM into one event grid.
 *
 * Lab called getPlayerBooksForBackend + Artemis getPlayersBackendNetExpo.
 * Strix books already carry PB groups on getBooksForBackend — split into
 * matchOddsPrem / dataPrem with Strix (BF)/(CM) labels (not Lab (PB)).
 */

import { isPremiumBmMarket } from '@/features/net-exposure/productScope'
import { labeledMarketName } from '@/features/net-exposure/marketSourceLabel'
import type { NetExposureEvent, NetExposureMarket } from '@/features/net-exposure/fixture'

/** Multi Bet → Genie Combo Special; source tag BF/CM (never PB). */
export function premiumMarketLabel(
  marketName: string,
  market?: Pick<NetExposureMarket, 'marketId' | 'isMatchOdds' | 'bookMakerforNetExposure' | 'bookmakerFlag' | 'key'>,
): string {
  const trimmed = marketName.trim()
  const base = /^multi bet$/i.test(trimmed) ? 'Genie Combo Special' : trimmed
  return labeledMarketName({
    marketId: market?.marketId,
    marketName: base,
    isMatchOdds: market?.isMatchOdds,
    bookMakerforNetExposure: market?.bookMakerforNetExposure,
    bookmakerFlag: market?.bookmakerFlag,
    key: market?.key,
  })
}

function withSourceLabel(market: NetExposureMarket): NetExposureMarket {
  return { ...market, marketName: premiumMarketLabel(market.marketName, market) }
}

function isMatchOddsMarket(m: NetExposureMarket): boolean {
  return Boolean(m.isMatchOdds) || /^match odds$/i.test(m.marketName.trim())
}

/** Lab skips fancy rows whose eventName is Premium Fancy. */
function keepPremiumFancyRow(m: NetExposureMarket): boolean {
  return m.eventName !== 'Premium Fancy'
}

/**
 * Split sportsbook vs Premium BM markets the way Lab's getInsideDataMethod merge did.
 */
export function toMemberPlayerBooksLayout(events: NetExposureEvent[]): NetExposureEvent[] {
  return events
    .map((event) => {
      const allMarkets: NetExposureMarket[] = [
        ...(event.matchOdds ? [event.matchOdds] : []),
        ...event.data,
        ...(event.matchOddsPrem ? [event.matchOddsPrem] : []),
        ...(event.dataPrem ?? []),
      ]

      const sports = allMarkets.filter((m) => !isPremiumBmMarket(m))
      const premium = allMarkets.filter((m) => isPremiumBmMarket(m))

      const matchOdds = sports.find(isMatchOddsMarket)
      const data = sports.filter((m) => m !== matchOdds)

      const matchOddsPremRaw = premium.find(isMatchOddsMarket)
      const dataPrem = premium
        .filter((m) => m !== matchOddsPremRaw)
        .filter(keepPremiumFancyRow)
        .map(withSourceLabel)

      const matchOddsPrem = matchOddsPremRaw ? withSourceLabel(matchOddsPremRaw) : undefined

      return {
        ...event,
        matchOdds,
        data,
        matchOddsPrem,
        dataPrem,
      }
    })
    .filter(
      (e) =>
        Boolean(e.matchOdds) ||
        e.data.length > 0 ||
        Boolean(e.matchOddsPrem) ||
        (e.dataPrem?.length ?? 0) > 0,
    )
}
